/*----------------------------------
- LANDING WEBSITE ROUTES (SSOT)
----------------------------------*/

import { EDITION_PAGES } from '@uniquedomains/components/contracts/editions/public';
import { SALES_PATH } from '@uniquedomains/components/contracts/sales/public';
import { AUCTIONS_PATH } from '@uniquedomains/components/contracts/auctions/public';
import { REGISTRARS_PATH } from '@uniquedomains/components/contracts/registrars/public';
import { AVAILABILITY_PATH, WHOIS_PATH } from '@uniquedomains/components/contracts/lookup/limits';
import { PRODUCT_PAGE_ROUTES } from '@/common/catalogs/appRoutesCatalog';
import { WEBSITE_DOMAINS_BASE_PATH } from '@/common/libs/filters';
import { productFeaturePagePath } from '@/common/catalogs/landing/productPages';
import { solutionPagePath } from '@/common/catalogs/landing/solutionPages';
import { DOMAINING_NEWS_INDEX_PATH } from '@uniquedomains/components/contracts/news/public';
import { NEWSLETTER_PAGE_PATH } from '@uniquedomains/components/contracts/newsletter/public';
import { AGENDA_PATH } from '@uniquedomains/components/contracts/agenda/public';
import { TLD_APPLICATIONS_PATH, TLD_HUB_PATH } from '@uniquedomains/components/contracts/tlds/public';

/*----------------------------------
- PAGE ROUTES
----------------------------------*/

export const LANDING_WEBSITE_PAGE_ROUTES = {
    home: '/',
    agenda: AGENDA_PATH,
    apiDocs: '/api',
    compare: '/compare',
    domainingNews: DOMAINING_NEWS_INDEX_PATH,
    glossary: '/glossary',
    newsletter: NEWSLETTER_PAGE_PATH,
    pricing: '/pricing',
    privacy: '/privacy',
    productAppraise: productFeaturePagePath('appraise'),
    productRadars: productFeaturePagePath('radars'),
    productSearch: productFeaturePagePath('search'),
    productVerify: productFeaturePagePath('verify'),
    productWatchlists: productFeaturePagePath('watchlists'),
    refundPolicy: '/refund-policy',
    solutionFounders: solutionPagePath('founders'),
    solutionInvestors: solutionPagePath('investors'),
    team: '/team',
    terms: '/terms',
    // `agenda`, `tldApplications` and `tldHub` come from the shared contracts (ADR-0054, ADR-0055);
    // a TLD insight page is `tldInsightPath(slug)`, never a key here.
    tldApplications: TLD_APPLICATIONS_PATH,
    tldHub: TLD_HUB_PATH,
    // The sales page (ADR-0067).
    sales: SALES_PATH,
    // The auctions page (ADR-0076).
    auctions: AUCTIONS_PATH,
    // The registrar hub (ADR-0079); a profile is `registrarProfilePath(slug)`, never a key here.
    // Product has its own `/registrars`, so Product's list of website pages never names it.
    registrars: REGISTRARS_PATH,
    // The lookup tools' website twins (ADR-0078); Product has its own `/whois` and `/availability`,
    // so Product's list of website pages never names them (they stay signup return targets).
    whois: WHOIS_PATH,
    availability: AVAILABILITY_PATH,
    // The daily editions (ADR-0074), one page named per persona; `/product/opportunities` 301s here.
    trendingDomains: EDITION_PAGES.investor.path,
    businessIdeas: EDITION_PAGES.founder.path,
} as const;

export const WEBSITE_PAGES: readonly string[] = Object.values(LANDING_WEBSITE_PAGE_ROUTES);

/*----------------------------------
- LINK CATALOG
----------------------------------*/

export const LANDING_WEBSITE_LINKS_CATALOG = {
    productBaseUrl: PRODUCT_PAGE_ROUTES.root,
    productDomains: PRODUCT_PAGE_ROUTES.domains,
    productAppraise: LANDING_WEBSITE_PAGE_ROUTES.productAppraise,
    trendingDomains: LANDING_WEBSITE_PAGE_ROUTES.trendingDomains,
    businessIdeas: LANDING_WEBSITE_PAGE_ROUTES.businessIdeas,
    productRadars: LANDING_WEBSITE_PAGE_ROUTES.productRadars,
    productWatchlists: LANDING_WEBSITE_PAGE_ROUTES.productWatchlists,
    createRadar: PRODUCT_PAGE_ROUTES.domains,
    productSearch: LANDING_WEBSITE_PAGE_ROUTES.productSearch,
    productVerify: LANDING_WEBSITE_PAGE_ROUTES.productVerify,
    screener: WEBSITE_DOMAINS_BASE_PATH,
    solutionFounder: LANDING_WEBSITE_PAGE_ROUTES.solutionFounders,
    solutionInvestor: LANDING_WEBSITE_PAGE_ROUTES.solutionInvestors,
    // Every successful website auth lands on the Product Screener, whatever the
    // persona. These two record that, rather than two different promises.
    solutionFounderSignup: PRODUCT_PAGE_ROUTES.domains,
    solutionInvestorSignup: PRODUCT_PAGE_ROUTES.domains,
    signIn: `${PRODUCT_PAGE_ROUTES.root}?auth=login`,
    apiDocs: LANDING_WEBSITE_PAGE_ROUTES.apiDocs,
    compare: LANDING_WEBSITE_PAGE_ROUTES.compare,
    glossary: LANDING_WEBSITE_PAGE_ROUTES.glossary,
    newsletter: LANDING_WEBSITE_PAGE_ROUTES.newsletter,
    pricing: LANDING_WEBSITE_PAGE_ROUTES.pricing,
    freeTools: WEBSITE_DOMAINS_BASE_PATH,
    github: 'https://github.com/uniquedomains',
    githubLists: 'https://github.com/uniquedomains',
} as const;
